import React, { useState } from 'react';
import axios from 'axios';
import { Percent, Loader2, Pencil, X } from 'lucide-react';
import { toast } from './ui/sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FONT = { fontFamily: 'IBM Plex Sans, sans-serif' };
export const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const describeFeeRule = (type, value) => (type === 'percent' ? `${Number(value || 0)}% of gross pay` : `${money(value)} per statement`);

export const describeOverride = (o) => {
  if (!o || !o.mode || o.mode === 'default') return null;
  if (o.mode === 'waived') return `Waived${o.note ? ` — ${o.note}` : ''}`;
  return `${describeFeeRule(o.mode, o.value)}${o.note ? ` — ${o.note}` : ''}`;
};

/** Mode / value / note picker shared by the generate form and the payroll-detail override editors. */
export const FeeRulePicker = ({ value, onChange, idPrefix = 'fee', defaultLabel }) => {
  const v = value || { mode: 'default', value: '', note: '' };
  const set = (patch) => onChange({ ...v, ...patch });
  return (
    <div className="flex flex-wrap items-center gap-2" style={FONT}>
      <select data-testid={`${idPrefix}-mode`} value={v.mode} onChange={(e) => set({ mode: e.target.value })} className="px-2 py-1.5 border border-slate-300 rounded text-xs bg-white">
        <option value="default">{defaultLabel || 'Use default'}</option>
        <option value="fixed">Fixed amount ($)</option>
        <option value="percent">% of gross pay</option>
        <option value="waived">Waive ($0)</option>
      </select>
      {(v.mode === 'fixed' || v.mode === 'percent') && (
        <div className="relative">
          <input data-testid={`${idPrefix}-value`} type="number" min="0" max={v.mode === 'percent' ? 100 : 10000} step="0.01" value={v.value}
            onChange={(e) => set({ value: e.target.value })} placeholder={v.mode === 'percent' ? '%' : '$'} className="w-28 px-2 py-1.5 border border-slate-300 rounded text-xs" />
          {v.mode === 'percent' && <Percent className="w-3 h-3 absolute right-2 top-2 text-slate-400" />}
        </div>
      )}
      {v.mode !== 'default' && (
        <input data-testid={`${idPrefix}-note`} type="text" maxLength={300} value={v.note || ''} onChange={(e) => set({ note: e.target.value })}
          placeholder="Optional note (shown on paystub)" className="flex-1 min-w-[180px] px-2 py-1.5 border border-slate-300 rounded text-xs" />
      )}
    </div>
  );
};

export const feeRuleToBody = (v) => {
  if (!v || v.mode === 'default') return { mode: 'default' };
  if (v.mode === 'waived') return { mode: 'waived', note: v.note || null };
  return { mode: v.mode, value: Number(v.value || 0), note: v.note || null };
};

/** Explanation shown next to a $0 / waived fee. */
export const feeExplanation = (entry) => {
  const d = entry.admin_fee_detail || {};
  if (d.explanation) return d.explanation;
  const r = entry.admin_fee_reason;
  if (!r) return 'No administration fee recorded for this statement';
  return { not_authorized: 'Not authorized for this payee', waived: 'Waived for this payee', waived_payroll: 'Waived for this payroll run',
    waived_driver: 'Waived for this payee on this payroll', disabled: 'Configured amount is $0.00', applied: 'Charged once per pay statement' }[r] || r;
};

const useFeeOverride = (payroll, onApplied) => {
  const [busy, setBusy] = useState(false);
  const apply = async (body) => {
    setBusy(true);
    try {
      const res = await axios.put(`${API}/payrolls/${payroll.id}/admin-fee`, body, { withCredentials: true });
      toast.success(res.data?.revision_of ? 'Fee updated — a pending revision draft was created. Drivers still see the sent version until you send it.' : 'Administration fee updated and payroll recalculated.');
      onApplied?.(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update the administration fee');
    } finally { setBusy(false); }
  };
  return { busy, apply };
};

/** Header card on the payroll detail page: total admin fees + run-level override. */
export const AdminFeeSummaryCard = ({ payroll, entries, isAdmin, onApplied }) => {
  const [editing, setEditing] = useState(false);
  const [rule, setRule] = useState({ mode: payroll.admin_fee_override?.mode || 'default', value: payroll.admin_fee_override?.value ?? '', note: payroll.admin_fee_override?.note || '' });
  const { busy, apply } = useFeeOverride(payroll, onApplied);
  const total = Number(payroll.total_admin_fees ?? entries.reduce((s, e) => s + Number(e.admin_fee_amount || 0), 0));
  const charged = entries.filter((e) => Number(e.admin_fee_amount || 0) > 0).length;
  const runRule = describeOverride(payroll.admin_fee_override);
  const canEdit = isAdmin && !payroll.locked && payroll.status !== 'superseded';
  return (
    <div data-testid="admin-fee-summary-card" className="border border-violet-200 bg-violet-50 rounded-md p-4 mb-4" style={FONT}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-violet-700 mb-1">Administration fee</p>
          <p data-testid="admin-fee-total" className="text-xl font-bold text-violet-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>{money(total)}</p>
          <p className="text-xs text-violet-800 mt-0.5" data-testid="admin-fee-rule-text">
            {charged} of {entries.length} payee{entries.length === 1 ? '' : 's'} charged · {runRule ? `This run: ${runRule}` : 'Rule: company default / per-payee settings'}
            {payroll.admin_fee_override?.set_by_name ? ` · set by ${payroll.admin_fee_override.set_by_name}` : ''}
          </p>
        </div>
        {canEdit && !editing && (
          <button type="button" data-testid="admin-fee-edit-run" onClick={() => setEditing(true)} className="flex items-center gap-1 text-xs font-semibold text-violet-900 border border-violet-300 bg-white rounded-md px-2.5 py-1.5 hover:bg-violet-100">
            <Pencil className="w-3.5 h-3.5" /> Override for this run
          </button>
        )}
      </div>
      {editing && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <FeeRulePicker value={rule} onChange={setRule} idPrefix="run-fee" defaultLabel="Use company / payee defaults" />
          <button type="button" disabled={busy} data-testid="run-fee-apply" onClick={() => apply({ scope: 'run', ...feeRuleToBody(rule) })} className="flex items-center gap-1 text-xs font-semibold text-white bg-[#002FA7] rounded-md px-3 py-1.5 disabled:opacity-50">
            {busy && <Loader2 className="w-3 h-3 animate-spin" />} Apply & recalculate
          </button>
          <button type="button" data-testid="run-fee-cancel" onClick={() => setEditing(false)} className="text-xs text-slate-600 px-2 py-1.5"><X className="w-4 h-4" /></button>
          <p className="w-full text-[11px] text-violet-800">Applies to every authorized payee in this run (a payee's standing waiver still wins). Recalculating a sent payroll creates a pending revision — nothing is emailed until you send it.</p>
        </div>
      )}
    </div>
  );
};

/** Per-payee admin fee row inside an expanded payroll entry. */
export const EntryAdminFeeRow = ({ payroll, entry, isAdmin, onApplied }) => {
  const key = `${entry.entry_kind === 'staff' ? 'staff' : 'driver'}:${entry.entry_kind === 'staff' ? entry.staff_profile_id || entry.driver_id : entry.driver_id}`;
  const existing = (payroll.admin_fee_entry_overrides || {})[key];
  const [editing, setEditing] = useState(false);
  const [rule, setRule] = useState({ mode: existing?.mode || 'default', value: existing?.value ?? '', note: existing?.note || '' });
  const { busy, apply } = useFeeOverride(payroll, onApplied);
  const amt = Number(entry.admin_fee_amount || 0);
  const canEdit = isAdmin && !payroll.locked && payroll.status !== 'superseded' && entry.admin_fee_reason !== 'not_authorized';
  return (
    <div data-testid={`entry-admin-fee-${entry.id}`} className="p-3 bg-white border border-violet-200 rounded-md" style={FONT}>
      <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
        <span className="text-slate-700">
          <span className="font-semibold">Administration fee</span>
          <span className="text-xs text-slate-500 ml-2">({feeExplanation(entry)})</span>
          {existing && <span className="ml-2 px-1.5 py-0.5 rounded bg-violet-100 text-violet-800 text-[10px] font-bold uppercase">Overridden for this payroll</span>}
        </span>
        <span className="flex items-center gap-2">
          <span data-testid={`entry-admin-fee-amount-${entry.id}`} className={amt > 0 ? 'text-orange-700 font-medium' : 'text-slate-500 font-medium'}>{amt > 0 ? `− ${money(amt)}` : '$0.00'}</span>
          {canEdit && !editing && (
            <button type="button" data-testid={`entry-admin-fee-edit-${entry.id}`} onClick={() => setEditing(true)} className="text-xs text-[#002FA7] hover:underline">Override</button>
          )}
        </span>
      </div>
      {editing && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <FeeRulePicker value={rule} onChange={setRule} idPrefix={`entry-fee-${entry.id}`} defaultLabel="Use run / payee default" />
          <button type="button" disabled={busy} data-testid={`entry-fee-apply-${entry.id}`} onClick={() => apply({ scope: 'payee', payee_type: key.split(':')[0], payee_id: key.split(':')[1], ...feeRuleToBody(rule) })} className="flex items-center gap-1 text-xs font-semibold text-white bg-[#002FA7] rounded-md px-3 py-1.5 disabled:opacity-50">
            {busy && <Loader2 className="w-3 h-3 animate-spin" />} Apply
          </button>
          <button type="button" onClick={() => setEditing(false)} className="text-xs text-slate-600 px-2 py-1.5"><X className="w-4 h-4" /></button>
        </div>
      )}
    </div>
  );
};
